import React from 'react';
import {
    Box, Typography, Autocomplete, TextField, CircularProgress,
    Accordion, AccordionSummary, AccordionDetails, Chip, Divider,
    Paper, Alert
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import swal from 'sweetalert';
import Colors from '../../../components/ui/Colors';
import CustomLoader from '../../../components/CustomLoader';
import { getReportTraceSummaries, getReportTrace } from '../../../utilityFunctions/AdminUtil';

const dateOptions = {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
};

const formatDate = (isoString) => {
    if (!isoString) return '—';
    try {
        return new Date(isoString).toLocaleString(undefined, dateOptions);
    } catch {
        return isoString;
    }
};

const PrettyJson = ({ json }) => {
    if (!json) return <Typography variant="body2" color="text.secondary">—</Typography>;
    let parsed = json;
    if (typeof json === 'string') {
        try { parsed = JSON.parse(json); } catch { /* show as plain text */ }
    }
    if (typeof parsed === 'string') {
        return (
            <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.78rem', color: 'text.secondary' }}>
                {parsed}
            </Box>
        );
    }
    return (
        <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.78rem', color: 'text.secondary' }}>
            {JSON.stringify(parsed, null, 2)}
        </Box>
    );
};

const PlainText = ({ text }) => {
    if (!text) return <Typography variant="body2" color="text.secondary">—</Typography>;
    return (
        <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.82rem', lineHeight: 1.6 }}>
            {text}
        </Box>
    );
};

const SectionLabel = ({ label }) => (
    <Typography variant="overline" sx={{ color: Colors.primary, fontWeight: 600, display: 'block', mt: 1.5, mb: 0.5 }}>
        {label}
    </Typography>
);

const SqlSectionState = ({ state }) => (
    <Box>
        <SectionLabel label="SQL Query" />
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f8f8f8', mb: 1 }}>
            <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.8rem', fontFamily: 'monospace', color: '#1a1a1a' }}>
                {state.sql || '—'}
            </Box>
        </Paper>

        {state.executionError && (
            <>
                <SectionLabel label="Execution Error" />
                <Alert severity="error" sx={{ mb: 1, fontSize: '0.82rem' }}>
                    {state.executionError}
                </Alert>
            </>
        )}

        <SectionLabel label="Prompt" />
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa', mb: 1 }}>
            <PlainText text={state.promptText} />
        </Paper>

        <SectionLabel label="Payload" />
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa' }}>
            <PrettyJson json={state.payloadJson} />
        </Paper>
    </Box>
);

const GenericState = ({ state }) => (
    <Box>
        <SectionLabel label="Prompt" />
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa', mb: 1 }}>
            <PlainText text={state.promptText} />
        </Paper>

        <SectionLabel label="Payload" />
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#fafafa' }}>
            <PrettyJson json={state.payloadJson} />
        </Paper>
    </Box>
);

const StateAccordion = ({ state, defaultExpanded }) => {
    const isSqlSection = state.stateType === 'sql_section';
    const hasError = isSqlSection && !!state.executionError;

    return (
        <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ mb: 0.5, border: '1px solid', borderColor: hasError ? 'error.light' : 'divider', borderRadius: '8px !important', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: hasError ? '#fff5f5' : 'background.paper', borderRadius: '8px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{state.stateName}</Typography>
                    {hasError && <Chip label="Error" color="error" size="small" />}
                    <Typography variant="caption" color="text.secondary">{formatDate(state.createdAt)}</Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 1 }}>
                {isSqlSection ? <SqlSectionState state={state} /> : <GenericState state={state} />}
            </AccordionDetails>
        </Accordion>
    );
};

const StepAccordion = ({ step }) => {
    const isSection = step.stepType === 'section';
    const hasErrors = isSection && step.states && step.states.some(s => s.stateType === 'sql_section' && s.executionError);

    return (
        <Accordion defaultExpanded disableGutters sx={{ mb: 1, border: '1px solid', borderColor: hasErrors ? 'warning.light' : 'divider', borderRadius: '12px !important', '&:before': { display: 'none' }, boxShadow: '0 2px 6px rgba(0,0,0,0.06)' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'background.paper', borderRadius: '12px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, color: Colors.primary }}>{step.stepName}</Typography>
                    {hasErrors && <Chip label="SQL errors" color="warning" size="small" />}
                    {step.createdAt && <Typography variant="caption" color="text.secondary">{formatDate(step.createdAt)}</Typography>}
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                {isSection ? (
                    <Box>
                        {step.states && step.states.map((state, idx) => (
                            <StateAccordion key={idx} state={state} defaultExpanded={false} />
                        ))}
                    </Box>
                ) : (
                    <GenericState state={step} />
                )}
            </AccordionDetails>
        </Accordion>
    );
};

export default function ReportTracePage(props) {
    const { loginUser } = props;

    const [loading, setLoading] = React.useState(false);
    const [loadingTrace, setLoadingTrace] = React.useState(false);
    const [reports, setReports] = React.useState([]);
    const [selectedReport, setSelectedReport] = React.useState(null);
    const [trace, setTrace] = React.useState(null);

    React.useEffect(() => {
        const load = async () => {
            setLoading(true);
            const data = await getReportTraceSummaries(loginUser.jwtToken);
            setLoading(false);
            if (!data) {
                swal({ title: 'Error', text: 'Failed to load reports', icon: 'error', button: 'OK' });
                return;
            }
            setReports(data);
        };
        load();
    }, []);

    const handleReportSelect = async (_, value) => {
        setSelectedReport(value);
        setTrace(null);
        if (!value) return;

        setLoadingTrace(true);
        const data = await getReportTrace(value.id, loginUser.jwtToken);
        setLoadingTrace(false);

        if (!data) {
            swal({ title: 'Error', text: 'Failed to load trace for this report', icon: 'error', button: 'OK' });
            return;
        }
        setTrace(data);
    };

    const getOptionLabel = (option) => {
        const parts = [];
        if (option.reportTitle) parts.push(option.reportTitle);
        if (option.email) parts.push(option.email);
        if (option.createdAt) parts.push(formatDate(option.createdAt));
        if (option.status) parts.push(`[${option.status}]`);
        return parts.join(' · ') || `Report #${option.id}`;
    };

    const statusColor = (status) => {
        switch (status) {
            case 'COMPLETED': return 'success';
            case 'FAILED': return 'error';
            case 'RUNNING': return 'info';
            default: return 'default';
        }
    };

    if (loading) return <CustomLoader />;

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1100px', mx: 'auto', marginTop: "5rem"}}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: Colors.primary, mb: 1 }}>
                Report Pipeline Traces
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Select a report to inspect the step-by-step pipeline execution, prompts, and LLM outputs.
            </Typography>

            <Autocomplete
                options={reports}
                getOptionLabel={getOptionLabel}
                onChange={handleReportSelect}
                value={selectedReport}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Search report by title, email, or date"
                        variant="outlined"
                        size="small"
                        sx={{ mb: 3 }}
                    />
                )}
                renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                        <Box sx={{ width: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                                    {option.reportTitle || `Report #${option.id}`}
                                </Typography>
                                <Chip label={option.status || 'UNKNOWN'} color={statusColor(option.status)} size="small" />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {option.email} · {formatDate(option.createdAt)}
                            </Typography>
                            {option.userRequest && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    "{option.userRequest.substring(0, 80)}{option.userRequest.length > 80 ? '…' : ''}"
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
                noOptionsText="No reports found"
                fullWidth
            />

            {loadingTrace && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={36} sx={{ color: Colors.primary }} />
                </Box>
            )}

            {trace && !loadingTrace && (
                <Box>
                    {/* Report header */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
                                {trace.steps.find(s => s.stepType === 'report_plan')?.reportTitle || `Report #${trace.reportId}`}
                            </Typography>
                            <Chip label={trace.status} color={statusColor(trace.status)} size="small" />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            <strong>User:</strong> {trace.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            <strong>Request:</strong> {trace.userRequest}
                        </Typography>
                    </Paper>

                    <Divider sx={{ mb: 2 }} />

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                        Pipeline Steps
                    </Typography>

                    {trace.steps.map((step, idx) => (
                        <StepAccordion key={idx} step={step} />
                    ))}
                </Box>
            )}

            {selectedReport && !trace && !loadingTrace && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                    No trace data available for this report.
                </Typography>
            )}
        </Box>
    );
}
